import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { DateTime } from 'luxon';

import { BottomSheet } from 'public/components/BottomSheet';
import { Dropdown } from 'public/components/Dropdown';
import { useClassName, useWindowSize } from 'common/hooks';
import { LazyDayPicker } from 'common/lazy';
import { SVGIcon } from 'common/components';

import YearMonthPicker from './YearMonthPicker';

import './calendar.less';

const MonthCaption = ({ date, onOpenPicker }) => {
  const className = useClassName('Calendar');

  return (
    <div className="DayPicker-Caption">
      <div>
        <button
          type="button"
          className={className('caption-button')}
          onClick={onOpenPicker}>
          {DateTime.fromJSDate(date).toFormat('MMMM yyyy')}
          <SVGIcon name="angle-down" size="mini" />
        </button>
      </div>
    </div>
  );
};

const Calendar = ({
  open,
  initialDate,
  updateSelectedDate,
  closeModal,
  isDateUnavailable,
  openModal,
  footer,
  pastOnly,
}) => {
  const className = useClassName('Calendar');
  const [calendarSelectedDate, setCalendarSelectedDate] = useState(
    initialDate ? DateTime.fromISO(initialDate).toJSDate() : null
  );
  const today = new Date();
  const { isMobile } = useWindowSize();
  const [displayedMonth, setDisplayedMonth] = useState(
    calendarSelectedDate || today
  );
  // pastOnly calendars can switch to year and month grids.
  const [view, setView] = useState('days');

  useEffect(() => {
    if (open) setView('days');
  }, [open]);

  useEffect(() => {
    if (!initialDate) return;

    setCalendarSelectedDate(DateTime.fromISO(initialDate).toJSDate());
    setDisplayedMonth(DateTime.fromISO(initialDate).toJSDate());
  }, [initialDate]);

  const isDateBeforeToday = (date) => {
    return new Date(date.toDateString()) < new Date(new Date().toDateString());
  };

  const isDateAfterToday = (date) => {
    return new Date(date.toDateString()) > new Date(new Date().toDateString());
  };

  const isDateOutOfRange = pastOnly ? isDateAfterToday : isDateBeforeToday;

  const toISO = (jsDate) => DateTime.fromJSDate(jsDate).toISODate();

  const content = (
    <div className={className('calendar')}>
      {view === 'days' ? (
        <LazyDayPicker
          className="day-picker"
          disabledDays={[
            pastOnly ? { after: today } : { before: today },
            (jsDate) => isDateUnavailable(toISO(jsDate)),
          ]}
          enableOutsideDaysClick={false}
          selectedDays={calendarSelectedDate}
          fixedWeeks
          {...(pastOnly
            ? {
                month: displayedMonth,
                toMonth: today,
                onMonthChange: setDisplayedMonth,
                captionElement: (
                  <MonthCaption onOpenPicker={() => setView('years')} />
                ),
              }
            : { month: calendarSelectedDate })}
          onDayClick={(date) => {
            if (isDateOutOfRange(date) || isDateUnavailable(toISO(date)))
              return;

            updateSelectedDate(date);
          }}
        />
      ) : (
        <YearMonthPicker
          view={view}
          date={displayedMonth}
          onChange={setDisplayedMonth}
          onViewChange={setView}
        />
      )}
      {footer}
    </div>
  );

  if (isMobile) {
    return (
      <BottomSheet
        onOpen={openModal}
        onClose={closeModal}
        title="Select Date"
        isOpen={open}
        content={content}
      />
    );
  }

  return <Dropdown isOpen={open} onClose={closeModal} content={content} />;
};

Calendar.propTypes = {
  initialDate: PropTypes.string,
  open: PropTypes.bool.isRequired,
  updateSelectedDate: PropTypes.func.isRequired,
  closeModal: PropTypes.func.isRequired,
  isDateUnavailable: PropTypes.func.isRequired,
  trigger: PropTypes.any,
  footer: PropTypes.any,
  openModal: PropTypes.func,
  // Only today and earlier can be picked, with year and month grids.
  pastOnly: PropTypes.bool,
};

Calendar.defaultProps = {
  isDateUnavailable: () => false,
  openModal: () => false,
};

export default Calendar;
